import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";

import { LoadSpinner } from "./LoadSpinner";

type AnimatedButtonContentProps = {
  loading: boolean;
  idleContent: React.ReactNode;
  loadingContent?: React.ReactNode;
  idleKey?: string;
  loadingKey?: string;
  spinnerSize?: number | string;
  minHeight?: string;
  transitionDuration?: number;
};

export const BUTTON_CONTENT_TRANSITION = {
  duration: 0.18,
  ease: "easeOut",
} as const;

export const ButtonSpinner: React.FC<{
  size?: number | string;
  className?: string;
}> = ({ size = 18, className }) => {
  return <LoadSpinner size={size} color="#ffffff" className={className} />;
};

export const ButtonContentViewport = styled.div<{ $minHeight?: string }>`
  position: relative;
  width: 100%;
  min-height: ${({ $minHeight }) => $minHeight || "24px"};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const ButtonContentMotion = styled(motion.div)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ButtonContentRow = styled.div`
  min-height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

export const AnimatedButtonContent: React.FC<AnimatedButtonContentProps> = ({
  loading,
  idleContent,
  loadingContent,
  idleKey = "idle",
  loadingKey = "loading",
  spinnerSize = 18,
  minHeight,
  transitionDuration = BUTTON_CONTENT_TRANSITION.duration,
}) => {
  const contentTransition = { ...BUTTON_CONTENT_TRANSITION, duration: transitionDuration };
  return (
    <ButtonContentViewport $minHeight={minHeight}>
      <AnimatePresence mode="wait" initial={false}>
        {loading ? (
          <ButtonContentMotion
            key={loadingKey}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={contentTransition}
          >
            {loadingContent || (
              <ButtonContentRow>
                <ButtonSpinner size={spinnerSize} />
              </ButtonContentRow>
            )}
          </ButtonContentMotion>
        ) : (
          <ButtonContentMotion
            key={idleKey}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={contentTransition}
          >
            <ButtonContentRow>{idleContent}</ButtonContentRow>
          </ButtonContentMotion>
        )}
      </AnimatePresence>
    </ButtonContentViewport>
  );
};
